/**
 * PendingDuelBanner — Eye-catching banner shown at the top of the home screen
 * when the user has pending duel challenges waiting for a response.
 * Designed to be unmissable — gold border, pulsing icon, action-oriented.
 */

import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Animated } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { Swords, ChevronRight } from 'lucide-react-native';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';

interface Props {
  count: number;
}

export default function PendingDuelBanner({ count }: Props) {
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const [pulse] = useState(() => new Animated.Value(1));

  useEffect(() => {
    if (count <= 0) return;
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1.15, duration: 600, useNativeDriver: false }),
        Animated.timing(pulse, { toValue: 1, duration: 600, useNativeDriver: false }),
      ])
    );
    anim.start();
    return () => anim.stop();
  }, [count]);

  if (count <= 0) return null;

  return (
    <TouchableOpacity
      style={[styles.banner, { borderColor: '#B8972E' }]}
      onPress={() => router.push('/(tabs)/duels' as any)}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={`${count} pending duel challenge${count > 1 ? 's' : ''}. Tap to respond.`}
    >
      <Animated.View style={{ transform: [{ scale: pulse }] }}>
        <Swords size={20} color="#B8972E" />
      </Animated.View>
      <View style={styles.textBlock}>
        <Text style={[styles.title, { color: c.text }]}>
          {count === 1 ? "You've been challenged!" : `${count} challenges waiting`}
        </Text>
        <Text style={[styles.subtitle, { color: '#B8972E' }]}>
          TAP TO RESPOND
        </Text>
      </View>
      <ChevronRight size={18} color="#B8972E" />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE.sm,
    paddingVertical: SPACE.md,
    paddingHorizontal: SPACE.lg,
    borderRadius: RADIUS.xs,
    borderWidth: 2,
    backgroundColor: '#B8972E10',
  },
  textBlock: { flex: 1, gap: 2 },
  title: { fontSize: FONT.base, fontFamily: TYPO.body.semibold },
  subtitle: { fontSize: FONT.xs, fontFamily: TYPO.subheading.bold, letterSpacing: 2, textTransform: 'uppercase' },
});
